import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Chess } from "chessops/chess";
import { parseFen } from "chessops/fen";
import { makeSan, parseSan } from "chessops/san";
import { parseSquare } from "chessops/util";
import EmojiPicker from "emoji-picker-react";
import { FC, MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Chessboard } from "react-chessboard";
import { BoardPosition, Piece, Square } from "react-chessboard/dist/chessboard/types";

import { Button, buttonVariants } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AddAnalysisInput, AnnotatedPliesInput, ArrowInput } from "@/gen/graphql";
import { Arrow, SquareHighlightColor } from "@/gen/realtime";
import { usePressedKeyboardKey } from "@/hooks/game-feed/usePressedKeyboardKey";
import { cn } from "@/utils";
import { generatePositionFen } from "@/utils/chess";

const LEFT_MOUSE_BUTTON = 0;
const RIGHT_MOUSE_BUTTON = 2;

type tEmojiSquares = {
    [key: string]: { piece: Piece; emoji: string; square: Square }[];
};

// TODO: Currently we only support annotations for the first ply. We should add
//  support for playing out a line and annotating it. The backend already supports
//  this.
const ChessboardAnalysisInput = ({
    analysis,
    setAnalysis,
}: {
    analysis: AddAnalysisInput;
    setAnalysis: (analysis: AddAnalysisInput) => void;
}) => {
    const [currentMouseOverSquare, setCurrentMouseOverSquare] = useState<Square | null>(null);
    const [displayLegend, setDisplayLegend] = useState<boolean>(false);
    const [selectedEmoji, setSelectedEmoji] = useState<string | undefined>(undefined);
    const [piecePoistions, setPiecePositions] = useState<BoardPosition | undefined>(undefined);

    const [currentPlyIndex, setCurrentPlyIndex] = useState<number>(0);
    const [emojiSquares, setEmojiSquares] = useState<tEmojiSquares | undefined>(undefined);
    const elementRef = useRef<HTMLDivElement>(null);

    const mousePosition = useMousePosition();

    const sanPlies = useMemo(() => {
        return analysis.annotatedPlies.map(ply => ply.san);
    }, [analysis.annotatedPlies]);

    const memoizedAnnotatedPlies = useMemo(
        () => analysis.annotatedPlies,
        [analysis.annotatedPlies]
    );

    useEffect(() => {
        if (piecePoistions != null) {
            const currentEmojies = memoizedAnnotatedPlies.find(
                ply => ply.plyIndex === currentPlyIndex
            )?.pieceEmojis;
            if (currentEmojies) {
                const initial_emojiSquares: tEmojiSquares = {};

                for (const pos in currentEmojies) {
                    if (piecePoistions[pos as Square]) {
                        const piece = piecePoistions[pos as Square];
                        const emoji = currentEmojies[pos];

                        if (piece) {
                            if (!initial_emojiSquares[piece]) {
                                initial_emojiSquares[piece] = [];
                            }

                            initial_emojiSquares[piece].push({
                                piece,
                                emoji,
                                square: pos as Square,
                            });
                        }
                    }
                }
                setEmojiSquares(initial_emojiSquares);
            } else {
                setEmojiSquares({});
            }
        }
    }, [memoizedAnnotatedPlies, currentPlyIndex, piecePoistions, setPiecePositions]);

    const [startSquare, setStartSquare] = useState<Square | null>(null);

    const pressedKey = usePressedKeyboardKey();

    const handleMouseDown: MouseEventHandler<HTMLDivElement> = e => {
        const isRightMouseButton = e.button === RIGHT_MOUSE_BUTTON;

        if (isRightMouseButton && currentMouseOverSquare) {
            setStartSquare(currentMouseOverSquare);
        }
    };

    const handleMouseOverSquare = useCallback(
        (square: Square) => {
            setCurrentMouseOverSquare(square);
        },
        [setCurrentMouseOverSquare]
    );

    const calculateEmojiPos = () => {
        if (elementRef.current) {
            const rect = elementRef.current.getBoundingClientRect();

            return {
                top: mousePosition.y ? mousePosition.y - rect.top + 10 : 10,
                left: mousePosition.x ? mousePosition.x - rect.left + 10 : 10,
            };
        }
    };
    const handleMouseUp: MouseEventHandler<HTMLDivElement> = useCallback(
        e => {
            const isLeftMouseButton = e.button === LEFT_MOUSE_BUTTON;
            const isRightMouseButton = e.button === RIGHT_MOUSE_BUTTON;

            if (isLeftMouseButton && pressedKey === "Control") {
                setAnalysis({
                    annotatedPlies: analysis.annotatedPlies.map(ply => ({
                        ...ply,
                        arrows: [],
                        highlightedSquares: {},
                    })),
                    startingPositionFen: analysis.startingPositionFen,
                });
            }

            if (startSquare === null || currentMouseOverSquare === null) {
                return;
            }

            const color =
                pressedKey === "Control"
                    ? "red"
                    : pressedKey === "Shift"
                      ? "green"
                      : pressedKey === "Alt"
                        ? "blue"
                        : "yellow";

            if (isRightMouseButton && startSquare === currentMouseOverSquare) {
                const newAnnotatedPlies = analysis.annotatedPlies.map(ply => {
                    if (ply.plyIndex === currentPlyIndex) {
                        const newHighlightedSquares = { ...ply.highlightedSquares };

                        if (newHighlightedSquares[startSquare] === color) {
                            delete newHighlightedSquares[startSquare];
                            return {
                                ...ply,
                                highlightedSquares: newHighlightedSquares,
                            };
                        } else {
                            newHighlightedSquares[startSquare] = color;
                        }

                        return {
                            ...ply,
                            highlightedSquares: newHighlightedSquares,
                        };
                    }

                    return ply;
                });

                setAnalysis({
                    ...analysis,
                    annotatedPlies: newAnnotatedPlies,
                });
            }

            if (isRightMouseButton && startSquare !== currentMouseOverSquare) {
                const newAnnotatedPlies = analysis.annotatedPlies.map(ply => {
                    if (ply.plyIndex === currentPlyIndex) {
                        const newArrows: ArrowInput[] = [
                            ...ply.arrows,
                            { from: startSquare, to: currentMouseOverSquare, color },
                        ];

                        return {
                            ...ply,
                            arrows: newArrows,
                        };
                    }

                    return ply;
                });

                setAnalysis({
                    ...analysis,
                    annotatedPlies: newAnnotatedPlies,
                });
            }

            setStartSquare(null);
        },
        [analysis, currentMouseOverSquare, currentPlyIndex, pressedKey, setAnalysis, startSquare]
    );

    const updateEmojiSquares = (piece: Piece, square: Square, emoji: string) => {
        updateEmojiSquaress(square, emoji);
        setEmojiSquares(prevState => {
            // If the piece exists in the state
            const pieceData = prevState ? prevState[piece] : undefined;
            if (pieceData) {
                const updatedPieceData = pieceData
                    .map(entry => {
                        // Check if the entry already has the given square
                        if (entry.square === square) {
                            return null; // Mark this entry for removal (to remove the square)
                        }
                        return entry;
                    })
                    .filter(Boolean); // Remove `null` values
                // If no matching square was found, add it with the new emoji
                const squareExists = pieceData.some(entry => entry.square === square);
                if (!squareExists) {
                    updatedPieceData.push({ emoji: emoji, square: square, piece: piece });
                }
                // If no entries remain, remove the piece
                if (updatedPieceData.length === 0) {
                    const { [piece]: _, ...rest } = prevState || {}; // Remove the piece from the object
                    return rest as tEmojiSquares; // Ensure valid return type
                }
                // Otherwise, update the piece with the modified squares
                return {
                    ...prevState,
                    [piece]: updatedPieceData,
                } as tEmojiSquares;
            }
            // If the piece doesn't exist, add it with the new emoji and square
            return {
                ...prevState,
                [piece]: [{ emoji: emoji, square: square, piece: piece }],
            } as tEmojiSquares;
        });
    };

    const updateEmojiSquaress = (square: Square, emoji: string) => {
        const newAnnotatedPlies: AnnotatedPliesInput[] = analysis.annotatedPlies.map(ply => {
            if (ply.plyIndex === currentPlyIndex) {
                const newPieceEmojies = { ...ply.pieceEmojis };

                if (newPieceEmojies[square] === emoji) {
                    delete newPieceEmojies[square];
                    return {
                        ...ply,
                        pieceEmojis: newPieceEmojies,
                    };
                } else {
                    newPieceEmojies[square] = emoji;
                }

                return {
                    ...ply,
                    pieceEmojis: newPieceEmojies,
                };
            }

            return ply;
        });
        setAnalysis({
            ...analysis,
            annotatedPlies: newAnnotatedPlies,
        });
    };

    const customSquareStyles = useMemo(
        () =>
            Object.keys(
                analysis.annotatedPlies.find(ply => ply.plyIndex === currentPlyIndex)
                    ?.highlightedSquares ?? {}
            ).reduce(
                (acc, square) => {
                    const highlightedSquare = analysis.annotatedPlies.find(
                        ply => ply.plyIndex === currentPlyIndex
                    )?.highlightedSquares[square as Square] as SquareHighlightColor;

                    const backgroundColor =
                        highlightedSquare === "green"
                            ? "#00C851"
                            : highlightedSquare === "red"
                              ? "#FF4444"
                              : highlightedSquare === "blue"
                                ? "#5AD7FF"
                                : highlightedSquare === "yellow"
                                  ? "#CCFF50"
                                  : "rgba(255, 255, 255, 0.8)";
                    return {
                        ...acc,
                        [square]: {
                            backgroundColor,
                        },
                    };
                },
                {} as Record<Square, { backgroundColor: string }>
            ),
        [analysis.annotatedPlies, currentPlyIndex]
    );
    const isPiece = (value: string): value is Piece => {
        return ["wP", "wN", "wB", "wR", "wQ", "wK", "bP", "bN", "bB", "bR", "bQ", "bK"].includes(
            value
        );
    };
    const customPieceStyles = useMemo(() => {
        const em = emojiSquares ?? {};
        return Object.keys(em).reduce(
            (acc, piece) => {
                if (isPiece(piece)) {
                    acc[piece] = ({ square }: { square?: Square }) => {
                        // Safely handle the possibility that square is undefined
                        if (!square) return <div />; // Return a valid ReactElement (empty div)

                        const emojiEntry = emojiSquares
                            ? emojiSquares[piece].find(entry => entry.square === square)
                            : undefined;
                        return (
                            <div className="relative">
                                <img
                                    className="w-full"
                                    src={`/pieces/${piece.toLowerCase()}.png`}
                                />
                                {emojiEntry && (
                                    <div className="absolute bottom-0 right-1 w-2/5 text-lg">
                                        {emojiEntry.emoji}
                                    </div>
                                )}
                            </div>
                        );
                    };
                }
                return acc;
            },
            {} as Record<Piece, (props: { square?: Square }) => JSX.Element>
        );
    }, [emojiSquares]);

    return (
        <div className="relative w-full p-2" ref={elementRef}>
            <AnalysisLegendDialog
                open={displayLegend}
                onOpenChange={() => setDisplayLegend(!displayLegend)}
            />

            {selectedEmoji ? (
                <div style={calculateEmojiPos()} className="absolute z-20 h-4 w-4">
                    {selectedEmoji}
                </div>
            ) : null}
            <div className="flex items-center space-x-4">
                <EmojiPickerPopover
                    selectedEmoji={selectedEmoji}
                    onSelectEmoji={setSelectedEmoji}
                />
                <div className="flex flex-row items-center gap-4">
                    <Button
                        onClick={() => setSelectedEmoji(undefined)}
                        variant="outline"
                        className={"w-12"}
                        disabled={!selectedEmoji}
                    >
                        {selectedEmoji}
                    </Button>
                    <div className="flex-1 text-sm">
                        {selectedEmoji
                            ? "Place your emoji on your desired piece(s)"
                            : "No emoji selected"}
                    </div>
                </div>
            </div>
            <div
                className="relative flex w-full flex-col items-start justify-center space-y-4"
                onContextMenu={e => e.preventDefault()} // Prevent the context menu from appearing on right-click
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
            >
                <Chessboard
                    position={generatePositionFen({
                        startingPosition: analysis.startingPositionFen,
                        sanPlies,
                        plyIndex: currentPlyIndex,
                    })}
                    getPositionObject={currPos => setPiecePositions(currPos)}
                    areArrowsAllowed={false}
                    showBoardNotation
                    customPieces={customPieceStyles}
                    customArrows={(
                        analysis.annotatedPlies.find(ply => ply.plyIndex === currentPlyIndex)
                            ?.arrows ?? []
                    ).map(arrow => [arrow.from as Square, arrow.to as Square, arrow.color])}
                    onMouseOverSquare={handleMouseOverSquare}
                    onSquareClick={(square, piece) => {
                        if (square && piece && selectedEmoji) {
                            updateEmojiSquares(piece, square, selectedEmoji);
                        }
                    }}
                    customSquareStyles={customSquareStyles}
                    arePiecesDraggable={true}
                    onPieceDrop={(source, target, _piece) => {
                        const setup = parseFen(analysis.startingPositionFen).unwrap();
                        const chess = Chess.fromSetup(setup).unwrap();

                        for (const ply of sanPlies) {
                            const move = parseSan(chess, ply);

                            if (!move) {
                                throw new Error(`Invalid move: ${ply}`);
                            }

                            chess.play(move);
                        }

                        try {
                            const move = {
                                from: parseSquare(source),
                                to: parseSquare(target),
                            } as const;

                            const san = makeSan(chess, move);

                            setAnalysis({
                                ...analysis,
                                annotatedPlies: [
                                    ...analysis.annotatedPlies,
                                    {
                                        plyIndex: currentPlyIndex + 1,
                                        san,
                                        arrows: [],
                                        highlightedSquares: {},
                                        pieceEmojis: {},
                                    },
                                ],
                            });
                            setCurrentPlyIndex(currentPlyIndex + 1);
                        } catch (e) {
                            console.log("Invalid move!");
                            return false;
                        }

                        return true;
                    }}
                />
                <div className="flex flex-row gap-4">
                    <Button
                        variant="outline"
                        onClick={() => setCurrentPlyIndex(0)}
                        disabled={currentPlyIndex === 0}
                    >
                        First
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setCurrentPlyIndex(currentPlyIndex - 1)}
                        disabled={currentPlyIndex === 0}
                    >
                        <CaretLeft />
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setCurrentPlyIndex(currentPlyIndex + 1)}
                        disabled={currentPlyIndex === sanPlies.length - 1}
                    >
                        <CaretRight />
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setCurrentPlyIndex(sanPlies.length - 1)}
                        disabled={currentPlyIndex === sanPlies.length - 1}
                    >
                        Latest
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => {
                            setCurrentPlyIndex(0);
                            setAnalysis({
                                ...analysis,
                                annotatedPlies: analysis.annotatedPlies.slice(0, 1).map(ply => ({
                                    ...ply,
                                    arrows: [],
                                    highlightedSquares: {},
                                })),
                            });
                        }}
                    >
                        Reset
                    </Button>
                </div>
            </div>
        </div>
    );
};

const AnalysisLegendDialog = ({
    open,
    onOpenChange,
}: {
    open: boolean;
    onOpenChange: () => void;
}) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <div className="flex flex-1 flex-row justify-end">
                <DialogTrigger
                    className={cn(
                        buttonVariants({
                            variant: "link",
                        })
                    )}
                >
                    Show analysis legend
                </DialogTrigger>
            </div>
            <DialogContent className="max-h-screen overflow-y-auto" aria-describedby={undefined}>
                <DialogHeader>
                    <div className="mr-4 flex items-center justify-between">
                        <DialogTitle>Legend</DialogTitle>
                    </div>
                </DialogHeader>
                <div className="flex flex-col gap-2">
                    <div className="text-sm">
                        Drag to draw arrows. Click on squares to highlight.
                    </div>
                    <div className="text-sm">Ctrl + Left-click to clear.</div>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="text-brand-yellow">Yellow</div>
                        <div className="text-xs font-semibold">Right-click</div>
                        <div className="text-extra-dangerLight">Red</div>
                        <div className="text-xs font-semibold">Ctrl + Right-click</div>
                        <div className="text-extra-successLight">Green</div>
                        <div className="text-xs font-semibold">Shift + Right-click</div>
                        <div className="text-brand-blue">Blue</div>
                        <div className="flex flex-col text-xs font-semibold">
                            <div className="text-sm font-semibold">
                                Mac:{" "}
                                <div className="text-xs font-semibold">Option + right-click</div>
                            </div>
                            <div className="text-sm font-semibold">
                                Windows:{" "}
                                <div className="text-xs font-semibold">Alt + Right-click</div>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ChessboardAnalysisInput;

type MousePosition = {
    x: number | null;
    y: number | null;
};
const useMousePosition = () => {
    const [mousePosition, setMousePosition] = useState<MousePosition>({ x: null, y: null });

    useEffect(() => {
        const updateMousePosition = (ev: MouseEvent) => {
            setMousePosition({ x: ev.clientX, y: ev.clientY });
        };

        window.addEventListener("mousemove", updateMousePosition);

        return () => {
            window.removeEventListener("mousemove", updateMousePosition);
        };
    }, []);

    return mousePosition;
};

const EmojiPickerPopover = ({
    selectedEmoji,
    onSelectEmoji,
}: {
    selectedEmoji: string | undefined;
    onSelectEmoji: (emoji: string | undefined) => void;
}) => {
    const [open, setOpen] = useState(false);
    return (
        <Popover modal open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild className="my-2">
                <Button variant="outline">{selectedEmoji ? "Change emoji" : "Add Emoji"}</Button>
            </PopoverTrigger>
            <PopoverContent side="top" className="w-fit overflow-y-auto">
                <EmojiPicker
                    onEmojiClick={el => {
                        onSelectEmoji(selectedEmoji === el.emoji ? undefined : el.emoji);
                        setOpen(false);
                    }}
                />
            </PopoverContent>
        </Popover>
    );
};

export const ViewChessBoardAnalysis: FC<{
    position:
        | {
              [k: string]: unknown;
          }
        | undefined;
    emojiSquares: Partial<Record<Square, string>> | undefined;
    highlightedSquares?: Partial<Record<Square, "red" | "green" | "blue" | "yellow">>;
    arrows?: Arrow[];
}> = ({ position, arrows, emojiSquares, highlightedSquares }) => {
    const [piecePoistions, setPiecePositions] = useState<BoardPosition | undefined>(undefined);

    const customSquareStyles = useMemo(
        () =>
            Object.keys(highlightedSquares ?? {}).reduce(
                (acc, square) => {
                    const highlightedSquare = highlightedSquares
                        ? (highlightedSquares[square as Square] as SquareHighlightColor)
                        : [];

                    const backgroundColor =
                        highlightedSquare === "green"
                            ? "#00C851"
                            : highlightedSquare === "red"
                              ? "#FF4444"
                              : highlightedSquare === "blue"
                                ? "#5AD7FF"
                                : highlightedSquare === "yellow"
                                  ? "#CCFF50"
                                  : "rgba(255, 255, 255, 0.8)";
                    return {
                        ...acc,
                        [square]: {
                            backgroundColor,
                        },
                    };
                },
                {} as Record<Square, { backgroundColor: string }>
            ),
        [highlightedSquares]
    );
    const isPiece = (value: string): value is Piece => {
        return ["wP", "wN", "wB", "wR", "wQ", "wK", "bP", "bN", "bB", "bR", "bQ", "bK"].includes(
            value
        );
    };
    const customPieceStyles = useMemo(() => {
        const em = emojiSquares ?? {};
        return Object.keys(em).reduce(
            (acc, piecePos) => {
                const piece = piecePoistions && piecePoistions[piecePos as Square];
                if (piecePoistions && piece && isPiece(piece)) {
                    acc[piece] = ({ square }: { square?: Square }) => {
                        // Safely handle the possibility that square is undefined
                        if (!square) return <div />; // Return a valid ReactElement (empty div)

                        const emojiEntry = emojiSquares ? emojiSquares[square] : undefined;
                        return (
                            <div className="relative">
                                <img
                                    className="w-full"
                                    src={`/pieces/${piece.toLowerCase()}.png`}
                                />
                                {emojiEntry && (
                                    <div className="absolute bottom-0 right-1 w-2/5 text-lg">
                                        {emojiEntry}
                                    </div>
                                )}
                            </div>
                        );
                    };
                }
                return acc;
            },
            {} as Record<Piece, (props: { square?: Square }) => JSX.Element>
        );
    }, [emojiSquares, piecePoistions]);

    return (
        <Chessboard
            position={position}
            showBoardNotation
            customPieces={customPieceStyles}
            getPositionObject={currPos => setPiecePositions(currPos)}
            customArrows={(arrows ?? []).map(arrow => [
                arrow.from as Square,
                arrow.to as Square,
                arrow.color,
            ])}
            arePiecesDraggable={false}
            customSquareStyles={customSquareStyles}
        />
    );
};
